"use client";

import { useUniqueId } from "@faire/react/hooks/useUniqueId";
import { cn } from "@faire/react/tools/cn";
import { parseUnit } from "@faire/react/tools/styleUtils";
import React from "react";

import Color from "../Color";

import "./native-styles.css";

export const SVGIcon: React.FC<
  SVGIcon.IProps & React.RefAttributes<SVGElement>
> = ({
  children,
  className,
  titleAccess,
  viewBox,
  ref,
  width,
  height,
  margin,
  fontSize,
  color,
  fill,
  style,
  component,
  ...rest
}) => {
  const labelId = useUniqueId("titleAccess-");

  const Component = component ?? "svg";
  const vars: Record<string, string> = {};

  if (color) {
    // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
    vars["--f-svg-color"] = Color[color] ?? color;
  }
  if (fill) {
    vars["--f-svg-fill"] = fill;
  }
  if (width) {
    vars["--f-svg-width"] = parseUnit(width);
  }
  if (height) {
    vars["--f-svg-height"] = parseUnit(height);
  }
  if (margin) {
    vars["--f-svg-margin"] = margin;
  }

  if (fontSize !== undefined) {
    vars["--f-svg-font-size"] = parseUnit(fontSize.toString());
  }

  return (
    <Component
      ref={ref}
      className={cn("svgicon", className, {
        "svgicon-font-size": fontSize !== undefined,
      })}
      focusable="false"
      viewBox={viewBox ?? "0 0 24 24"}
      aria-labelledby={titleAccess ? labelId : undefined}
      aria-hidden={titleAccess ? undefined : "true"}
      role={titleAccess ? "img" : "presentation"}
      style={{
        ...vars,
        ...style,
      }}
      {...rest}
    >
      {children}
      {titleAccess ? <title id={labelId}>{titleAccess}</title> : null}
    </Component>
  );
};

export namespace SVGIcon {
  export interface IProps extends Omit<
    React.SVGProps<SVGSVGElement>,
    "ref" | "direction"
  > {
    /**
     * The component used for the root node.
     * Either a string to use a DOM element or a component.
     */
    component?: React.ElementType;
    /**
     * Provides a human-readable title for the element that contains it.
     * Keep this value empty if the icon is decorative only
     * https://www.w3.org/TR/SVG-access/#Equivalent
     */
    titleAccess?: string;
    /**
     * The height of the SVG component element.
     * Override to only allow string values.
     */
    height?: string;
    /**
     * The width of the SVG component element.
     * Override to only allow string values.
     */
    width?: string;
    /**
     * The margin of the SVG component element.
     */
    margin?: string;
  }
}
